import {http, HttpResponse} from 'msw';
import {SetupServerApi} from 'msw/node';

import {all, isNil} from 'ramda';

import {FINANCING_ENDING_BULK_OPERATION_ITEM_STATE} from '../constants/FINANCING_ENDING_BULK_OPERATION_ITEM_STATE';
import {FINANCING_ENDING_BULK_OPERATION_STATE} from '../constants/FINANCING_ENDING_BULK_OPERATION_STATE';
import {FINANCING_ENDING_RECIPIENT_SOURCE} from '../constants/FINANCING_ENDING_RECIPIENT_SOURCE';
import {
  ListFinancingEndingBulkOperationItemsApiResponse,
  FinancingEndingBulkOperationItemResponseBody,
  FinancingEndingBulkOperationItemWire,
  FinancingEndingBulkOperationResponseBody,
  FinancingEndingBulkOperationWire,
  FinancingEndingPreflightResponseBody,
  FinancingEndingPreviewResponseBody,
  FinancingEndingSubmitBulkActionResponseBody,
} from '../types';

// Placeholder global code: the real skip-reason codes are allocated in the backend S4 MR.
const PLACEHOLDER_SKIP_REASON_CODE = 'DMS-TEST-SKIP-0001';

export const preflightMock: FinancingEndingPreflightResponseBody = {
  selectedCount: 2,
  outreachCandidateRows: 1,
  plannedCount: 1,
  skippedCount: 1,
  cap: 200,
  canSend: true,
  blockReason: null,
  rows: [
    {
      rowId: '3f6c1f1e-0d5a-4b8e-9a43-2a1d5c7e0b01',
      eligible: true,
      reasonCodes: [],
      recipientSource: FINANCING_ENDING_RECIPIENT_SOURCE.CUSTOMER,
      recipientName: 'Jan Novák',
      recipientEmail: 'jan.novak@example.com',
    },
    {
      rowId: '3f6c1f1e-0d5a-4b8e-9a43-2a1d5c7e0b02',
      eligible: false,
      reasonCodes: [PLACEHOLDER_SKIP_REASON_CODE],
      recipientSource: null,
      recipientName: null,
      recipientEmail: null,
    },
  ],
  defaultEmailCredentialsId: '7a1b2c3d-4e5f-4a6b-8c7d-9e0f1a2b3c4d', // gitleaks:allow — deterministic test fixture, not a real secret
  canManageTemplates: true,
};

export const previewMock: FinancingEndingPreviewResponseBody = {
  rowId: '3f6c1f1e-0d5a-4b8e-9a43-2a1d5c7e0b01',
  recipientEmail: 'jan.novak@example.com',
  subject: 'Financing of your vehicle ends soon',
  bodyHtml: '<p>Dear customer, the financing of your vehicle ends soon.</p>',
  missingPlaceholders: [],
};

export const submitBulkActionMock: FinancingEndingSubmitBulkActionResponseBody = {
  bulkOperationId: '9c8b7a6d-5e4f-4321-8a9b-0c1d2e3f4a5b',
};

export const bulkOperationMock: FinancingEndingBulkOperationResponseBody = {
  bulkOperationId: '9c8b7a6d-5e4f-4321-8a9b-0c1d2e3f4a5b',
  resourceId: 'BUSINESS_CASE',
  actionCode: 'sendFinancingEndingEmail',
  state: FINANCING_ENDING_BULK_OPERATION_STATE.PARTIALLY_DONE,
  totalCount: 2,
  doneCount: 1,
  failedCount: 0,
  skippedCount: 1,
  createdAt: '2026-10-07T08:00:00+00:00',
  finishedAt: '2026-10-07T08:01:00+00:00',
  errorCode: null,
  errorMessage: null,
};

export const bulkOperationItemsMock: ListFinancingEndingBulkOperationItemsApiResponse = [
  {
    recordId: '3f6c1f1e-0d5a-4b8e-9a43-2a1d5c7e0b01',
    recordLabel: 'Škoda Octavia',
    recordAvailable: true,
    state: FINANCING_ENDING_BULK_OPERATION_ITEM_STATE.DONE,
    errorCode: null,
    errorMessage: null,
    errorParams: null,
    processedAt: '2026-10-07T08:00:30+00:00',
  },
  {
    recordId: '3f6c1f1e-0d5a-4b8e-9a43-2a1d5c7e0b02',
    recordLabel: 'Ford Focus',
    recordAvailable: true,
    state: FINANCING_ENDING_BULK_OPERATION_ITEM_STATE.SKIPPED,
    errorCode: PLACEHOLDER_SKIP_REASON_CODE,
    errorMessage: null,
    errorParams: null,
    processedAt: '2026-10-07T08:00:31+00:00',
  },
];

export const rerunFailedMock: FinancingEndingSubmitBulkActionResponseBody = {
  bulkOperationId: '1a2b3c4d-5e6f-4a7b-8c9d-0e1f2a3b4c5d',
};

// The fixtures above are in the shape the client reads; these serialize one to the shape the server sends.
const toBulkOperationWire = ({
  templateName,
  mailbox,
  sentBy,
  emailOutcomeCounts,
  ...operation
}: FinancingEndingBulkOperationResponseBody): FinancingEndingBulkOperationWire => ({
  ...operation,
  outcomeCounts: isNil(emailOutcomeCounts)
    ? null
    : {
        SENT: emailOutcomeCounts.sent,
        FAILED: emailOutcomeCounts.failed,
        BLOCKED: emailOutcomeCounts.blocked,
        PENDING: emailOutcomeCounts.pending,
      },
  detail: all(isNil, [templateName, mailbox, sentBy])
    ? null
    : {templateName: templateName ?? null, mailbox: mailbox ?? null, sentBy: sentBy ?? null},
});

// The server sends the communication id with every item detail, also when a fixture names none.
const DEFAULT_SENT_MESSAGE_ID = '0f1e2d3c-4b5a-4968-8776-655443322110';

const toBulkOperationItemWire = ({
  emailOutcome,
  recipientName,
  recipientEmail,
  sentMessageId,
  ...item
}: FinancingEndingBulkOperationItemResponseBody): FinancingEndingBulkOperationItemWire => ({
  ...item,
  outcome: emailOutcome ?? null,
  detail: all(isNil, [recipientName, recipientEmail, sentMessageId])
    ? null
    : {
        recipientName: recipientName ?? null,
        recipientEmail: recipientEmail ?? null,
        sentMessageId: sentMessageId ?? DEFAULT_SENT_MESSAGE_ID,
      },
});

const preflightFinancingEndingEmailResponseInterceptor = (
  server: SetupServerApi,
  mockData: FinancingEndingPreflightResponseBody = preflightMock
) =>
  server.use(
    http.post('*/dms/v1/financing-ending/email/preflight', () => HttpResponse.json(mockData))
  );

const previewFinancingEndingEmailResponseInterceptor = (
  server: SetupServerApi,
  mockData: FinancingEndingPreviewResponseBody = previewMock
) =>
  server.use(
    http.post('*/dms/v1/financing-ending/email/preview', () => HttpResponse.json(mockData))
  );

const submitFinancingEndingBulkActionResponseInterceptor = (
  server: SetupServerApi,
  mockData: FinancingEndingSubmitBulkActionResponseBody = submitBulkActionMock
) =>
  server.use(
    http.post('*/dms/v1/bulk-operation/resource/BUSINESS_CASE/action/:actionCode', () =>
      HttpResponse.json(mockData, {status: 202})
    )
  );

const getFinancingEndingBulkOperationResponseInterceptor = (
  server: SetupServerApi,
  mockData: FinancingEndingBulkOperationResponseBody = bulkOperationMock
) =>
  server.use(
    http.get('*/dms/v1/bulk-operation/:bulkOperationId', () =>
      HttpResponse.json(toBulkOperationWire(mockData))
    )
  );

const listFinancingEndingBulkOperationItemsResponseInterceptor = (
  server: SetupServerApi,
  mockData: ListFinancingEndingBulkOperationItemsApiResponse = bulkOperationItemsMock
) =>
  server.use(
    http.get('*/dms/v1/bulk-operation/:bulkOperationId/item', () =>
      HttpResponse.json(mockData.map(toBulkOperationItemWire))
    )
  );

// Pass null to answer 204 (no failed items to rerun).
const rerunFailedFinancingEndingBulkOperationResponseInterceptor = (
  server: SetupServerApi,
  mockData: FinancingEndingSubmitBulkActionResponseBody | null = rerunFailedMock
) =>
  server.use(
    http.post('*/dms/v1/bulk-operation/:bulkOperationId/rerun-failed', () =>
      isNil(mockData)
        ? new HttpResponse(null, {status: 204})
        : HttpResponse.json(mockData, {status: 202})
    )
  );

export const financingEndingApiMock = {
  preflightMock,
  previewMock,
  submitBulkActionMock,
  bulkOperationMock,
  bulkOperationItemsMock,
  rerunFailedMock,
  toBulkOperationWire,
  toBulkOperationItemWire,
  preflightFinancingEndingEmailResponseInterceptor,
  previewFinancingEndingEmailResponseInterceptor,
  submitFinancingEndingBulkActionResponseInterceptor,
  getFinancingEndingBulkOperationResponseInterceptor,
  listFinancingEndingBulkOperationItemsResponseInterceptor,
  rerunFailedFinancingEndingBulkOperationResponseInterceptor,
};
